import { useInfiniteQuery, useQuery, InfiniteData } from '@tanstack/react-query';
import {
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  getCountFromServer,
  where,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

const PAGE_SIZE = 10;

export interface CityData extends Record<string, unknown> {
  id: string;
  name: string;
  state: string;
  stateFull: string;
  isCentered: boolean;
  coordinates: {
    lat: number;
    lng: number;
  };
  hasLandingPages: boolean;
}

interface UseCitiesDataProps {
  searchQuery?: string;
  organizationId?: string;
  page?: number;
}

interface PageData {
  cities: CityData[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasNext: boolean;
}

type QueryFnData = InfiniteData<PageData>;

export const useAllCitiesData = () => {
  return useQuery({
    queryKey: ['AllCities'],
    queryFn: async () => {
      try {
        const citiesRef = collection(db, 'cities');
        const snapshot = await getDocs(query(citiesRef, orderBy('name', 'asc')));

        const citiesWithPages = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const pagesRef = collection(doc.ref, 'pages');
            const pagesSnapshot = await getDocs(query(pagesRef, limit(1)));

            return {
              id: doc.id,
              name: data.name || '',
              state: data.state || '',
              stateFull: data.stateFull || '',
              coordinates: {
                lat: data.coordinates?.lat || 0,
                lng: data.coordinates?.lng || 0,
              },
              hasLandingPages: !pagesSnapshot.empty,
            } as CityData;
          })
        );

        return citiesWithPages;
      } catch (error) {
        console.error('Error fetching all cities:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 30,
  });
};

export const useCitiesData = ({
  searchQuery,
  organizationId,
  page = 0,
}: UseCitiesDataProps = {}) => {
  const countQuery = useQuery({
    queryKey: ['CitiesCount', organizationId],
    queryFn: async () => {
      const citiesRef = collection(db, 'cities');
      const snapshot = await getCountFromServer(citiesRef);
      return snapshot.data().count;
    },
    staleTime: 1000 * 60 * 30,
    enabled: Boolean(organizationId),
  });

  const dataQuery = useInfiniteQuery<PageData, Error, QueryFnData>({
    queryKey: ['Cities', searchQuery, organizationId, page],
    queryFn: async ({ pageParam }) => {
      try {
        const citiesRef = collection(db, 'cities');
        let q = query(citiesRef, orderBy('name', 'asc'));

        if (searchQuery) {
          q = query(
            citiesRef,
            where('name', '>=', searchQuery),
            where('name', '<=', searchQuery + '\uf8ff'),
            orderBy('name', 'asc')
          );
        }

        q = query(q, limit(PAGE_SIZE));
        if (pageParam) {
          q = query(q, startAfter(pageParam));
        }

        const snapshot = await getDocs(q);
        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

        const citiesWithPages = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const pagesRef = collection(doc.ref, 'pages');
            const pagesSnapshot = await getDocs(query(pagesRef, limit(1)));

            return {
              id: doc.id,
              name: data.name || '',
              state: data.state || '',
              stateFull: data.stateFull || '',
              coordinates: {
                lat: data.coordinates?.lat || 0,
                lng: data.coordinates?.lng || 0,
              },
              hasLandingPages: !pagesSnapshot.empty,
            } as CityData;
          })
        );

        return {
          cities: citiesWithPages,
          lastDoc,
          hasNext: snapshot.docs.length === PAGE_SIZE,
        };
      } catch (error) {
        console.error('Error fetching cities:', error);
        throw error;
      }
    },
    initialPageParam: null as QueryDocumentSnapshot | null,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.lastDoc : undefined),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: Boolean(organizationId),
  });

  const cities = dataQuery.data?.pages.flatMap((page) => page.cities) ?? [];

  return {
    ...dataQuery,
    cities,
    totalCount: countQuery.data || 0,
    isLoading: dataQuery.isLoading || countQuery.isLoading || dataQuery.isFetching,
  };
};
